import { useQuery } from '@tanstack/react-query';

import { getOrganisationInviteLink } from 'app/services/organisation';

export const organisationInviteLinkQueryKey = (organisationId: string): [string, string] => [
  'organisation-invite-link',
  organisationId,
];

export interface UseOrganisationInviteLinkResult {
  inviteLink: string | undefined;
  isLoading: boolean;
  isError: boolean;
}

// The organisation's invite link (POST /tools/short-url) — the short URL shown on
// Marketing Material > Download materials next to the QR code. Opening it nominates
// the patient to the organisation, so every surface that offers "the app link"
// (QR codes, marketing material, Plus Websites app link sections) MUST use this
// single value rather than store-specific links.
export default function useOrganisationInviteLink(
  organisationId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {}
): UseOrganisationInviteLinkResult {
  const {
    data,
    isInitialLoading: isLoading,
    isError,
  } = useQuery<string | null>({
    queryKey: organisationInviteLinkQueryKey(organisationId ?? ''),
    // react-query rejects an undefined result, and an organisation without a short
    // URL is a legitimate answer rather than a failure — normalise it to null.
    queryFn: async () => (await getOrganisationInviteLink({ chainId: organisationId })) || null,
    enabled: enabled && Boolean(organisationId),
    staleTime: 5 * 60 * 1000,
    meta: { errorMessage: 'Failed to load the app download link' },
  });

  return { inviteLink: data ?? undefined, isLoading, isError };
}
